<template>
	<section
		data-testid="document-preview"
		aria-label="Bestandsvoorbeeld"
		class="min-w-0 border-t bg-gray-50 p-4"
	>
		<div class="flex items-start justify-between gap-2 mb-3">
			<h2 class="min-w-0 text-sm font-medium [overflow-wrap:anywhere]">
				{{ document.filename }}
			</h2>
			<button
				type="button"
				aria-label="Voorbeeld sluiten"
				class="shrink-0 rounded border bg-white px-2 py-1 text-sm"
				@click="emit('close')"
			>
				Sluiten
			</button>
		</div>
		<p v-if="!supported" class="text-sm text-gray-600">
			Voor dit bestandstype is geen voorbeeld beschikbaar.
		</p>
		<p v-else-if="loading" role="status" class="text-sm text-gray-600">
			Voorbeeld laden…
		</p>
		<p v-else-if="error" role="alert" class="text-sm text-red-700">
			Het voorbeeld kan niet worden geladen. Sluit het voorbeeld en probeer
			opnieuw.
		</p>
		<FilePreviewFileBox
			v-else-if="fileUrl"
			:key="document.id"
			:file-url="fileUrl"
			:content-type="rendererContentType"
			:byte-size="document.byteSize"
			mode="full"
		/>
	</section>
</template>
<script setup lang="ts">
import { computed, ref, watch } from "vue";
import type { DossierDocument } from "@/composables/useLoanApplicationSortedDocuments";
import { useDossierDocumentUrlStore } from "@/store/useDossierDocumentUrlStore";
import FilePreviewFileBox from "@/components/file-preview/FilePreviewFileBox.vue";
import { getFileType } from "@/helpers/fileHelpers";
import { FileType } from "@/enums/FileType";
const props = defineProps<{ document: DossierDocument }>();
const emit = defineEmits<{ close: [] }>();
const fileUrl = ref<string>();
const loading = ref(false);
const error = ref(false);
const fileType = computed(() => getFileType(props.document));
// FilePreviewFileBox routes by MIME only. This hint selects its renderer;
// the URL and response MIME/bytes remain unchanged.
const rendererContentType = computed(() => {
	const mimeType = props.document.contentType
		?.split(";")[0]
		?.trim()
		.toLowerCase();
	// The shared classifier includes TIFF, but the browser image renderer does not support it consistently.
	if (
		mimeType === "image/tiff" ||
		mimeType === "image/x-tiff" ||
		/\.tiff?$/i.test(props.document.filename)
	)
		return undefined;
	switch (fileType.value) {
		case FileType.Pdf:
			return "application/pdf";
		case FileType.Svg:
			return "image/svg+xml";
		case FileType.Img:
			return props.document.contentType?.startsWith("image/")
				? props.document.contentType
				: "image/*";
		default:
			return undefined;
	}
});
const supported = computed(() => rendererContentType.value !== undefined);
watch(
	() => [props.document.id, rendererContentType.value] as const,
	async ([id], _, onCleanup) => {
		let active = true;
		onCleanup(() => {
			active = false;
		});
		fileUrl.value = undefined;
		error.value = false;
		loading.value = supported.value;
		if (!supported.value) return;
		try {
			const url = await useDossierDocumentUrlStore().getFileUrl(id);
			if (active) {
				fileUrl.value = url;
				error.value = !url;
			}
		} catch {
			if (active) error.value = true;
		} finally {
			if (active) loading.value = false;
		}
	},
	{ immediate: true, flush: "sync" },
);
</script>
